<template>
	<Application
		:name="$t('text.registrationTitle')"
		data-cy="registration-view"
		:loading="loading"
		:errorDetail="errorDetail"
		v-model:errorTitle="errorTitle"
		v-model:successTitle="successTitle"
		:successDetail="successDetail"
		class="main"
	>
		<template #navbar>
			<button @click="navigateBack">
				<v-icon>mdi-arrow-left</v-icon>
			</button>
			<Stepper
				:value="step"
				:steps="getSteps()"
			/>
		</template>

		<!-- Step 1 -->
		<v-row
			justify="center"
			v-show="step === 1"
		>
			<v-card
				id="card"
				class="page elevation-4"
			>
				<v-card-text style="margin:0px;padding:0px">
					<h1>{{ $t('text.registrationTitle') }}</h1>
					<p
						class="subTitle"
						v-html="$t('text.registrationSubTitle')"
						style="margin-left:0px"
					></p>
					<v-form>
						<div class="field left-border">
							<v-label>{{ $t('text.preferredLanguage') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-select
								variant="outlined"
								density="compact"
								autocomplete="off"
								:placeholder="`${$t('text.choose')}...`"
								:error-messages="preferredLanguageErrors"
								v-model="preferredLanguage"
								:items="clientLocales"
								:hide-details="preferredLanguageErrors.length === 0"
								data-cy="preferredLanguage"
								id="preferredLanguage"
								item-value="locale"
							>
								<template v-slot:item="{ props, item }">
									<v-list-item
										v-bind="props"
										class="preferred-language-list-item"
									>
										<div style="display: flex; align-items: center;">
											<LanguageFlag v-model="item.value" />
											<span style="margin-left: 10px;">{{ item.title }}</span>
										</div>
									</v-list-item>
								</template>
								<template v-slot:selection="{ item }">
									<div style="display: flex; align-items: center;">
										<LanguageFlag v-model="item.value" />
										<span style="margin-left: 10px;">{{ item.title }}</span>
									</div>
								</template>
							</v-select>
							<p
								class="helpText"
								v-html="$t('text.preferredLanguageHelp')"
							/>
						</div>
						<div
							class="field left-border"
							v-if="clientRequired"
						>
							<v-label>{{ $t('text.marketplace') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<div class="error">
								{{ $t('text.registrationErrorMissingClientId') }}
							</div>
							<v-select
								variant="outlined"
								density="compact"
								v-if="clients"
								autocomplete="off"
								:placeholder="$t('text.marketplace')"
								v-model="serviceProvider.marketplace"
								:error-messages="marketplaceErrors"
								:items="clients"
								item-title="fields.title.de"
								item-value="fields.title.de"
								data-cy="marketplace"
								id="registerMarketplace"
							/>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.personalName') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								required
								autocomplete="name"
								:hide-details="contactPersonNameErrors.length === 0"
								:error-messages="contactPersonNameErrors"
								@update:modelValue="v$.contactInfoName.fields.contactDetail.de.$touch()"
								@blur="v$.contactInfoName.fields.contactDetail.de.$touch()"
								v-model="contactInfoName.fields.contactDetail.de"
								data-cy="personalName"
								id="registerPersonalName"
							/>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.email') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								required
								type="email"
								autocomplete="email"
								:hide-details="contactPersonEmailErrors.length === 0"
								:error-messages="contactPersonEmailErrors"
								@update:modelValue="emailIsUnique = true; v$.serviceProvider.fields.contactPersonEmail.de.$touch()"
								@blur="deferredCheckEmailExists"
								v-model="serviceProvider.fields.contactPersonEmail.de"
								data-cy="email"
								id="registerEmail"
							/>
							<p
								class="helpText"
								v-html="$t('text.registrationEmailHelp')"
							/>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.password') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								required
								autocomplete="new-password"
								:type="showPassword ? 'text' : 'password'"
								v-model="serviceProvider.password"
								:error-messages="passwordErrors"
								:hint="passwordPolicyHint()"
								persistent-hint
								hide-details="auto"
								@update:modelValue="onPasswordInput"
								@blur="v$.serviceProvider.password.$touch()"
								data-cy="password"
								id="registerPassword"
							>
								<template v-slot:append-inner>
									<v-icon
										@click="showPassword = !showPassword"
										tabindex="-1"
										style="cursor: pointer"
									>
										{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
									</v-icon>
								</template>
							</v-text-field>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.repeatPassword') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								:type="showRepeatPassword ? 'text' : 'password'"
								variant="outlined"
								density="compact"
								required
								sameAs
								autocomplete="new-password"
								v-model="serviceProvider.repeatPassword"
								:error-messages="repeatPasswordErrors"
								:hide-details="repeatPasswordErrors.length === 0"
								@update:modelValue="v$.serviceProvider.repeatPassword.$touch()"
								@blur="v$.serviceProvider.repeatPassword.$touch()"
								data-cy="repeatPassword"
								id="registerPasswordRepeat"
							>
								<template v-slot:append-inner>
									<v-icon
										@click="showRepeatPassword = !showRepeatPassword"
										tabindex="-1"
										style="cursor: pointer"
									>
										{{ showRepeatPassword ? 'mdi-eye-off' : 'mdi-eye' }}
									</v-icon>
								</template>
							</v-text-field>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.contactPersonPhone', { format: phoneFormat }) }}</v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								required
								:hide-details="contactPersonPhoneErrors.length === 0"
								:error-messages="contactPersonPhoneErrors"
								@update:modelValue="v$.serviceProvider.fields.contactPersonPhone.de.$touch()"
								@blur="v$.serviceProvider.fields.contactPersonPhone.de.$touch()"
								v-model="serviceProvider.fields.contactPersonPhone.de"
								type="tel"
								autocomplete="tel"
								data-cy="contactPersonPhone"
								id="registerContactPersonPhone"
							/>
						</div>
						<v-row
							align="center"
							justify="center"
							style="margin-top:20px"
						>
							<v-btn
								id="registerStep2"
								data-cy="btn-step-2"
								variant="flat"
								theme="dark"
								class="activeButton"
								@click.prevent="next()"
							>{{ $t('text.nextStep') }}</v-btn>
						</v-row>
					</v-form>
				</v-card-text>
			</v-card>
		</v-row>

		<!-- Step 2 -->
		<v-row
			justify="center"
			v-show="step === 2"
		>
			<v-card
				id="card"
				class="page elevation-4"
			>
				<v-card-text style="margin:0px;padding:0px">
					<h1>{{ $t('text.myInfo') }}</h1><br />
					<v-form>
						<div class="field left-border">
							<v-label>{{ $t('text.businessName') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								required
								autocomplete="organization"
								:hide-details="businessNameErrors.length === 0"
								v-model="serviceProvider.fields.title.de"
								:error-messages="businessNameErrors"
								@update:modelValue="v$.serviceProvider.fields.title.de.$touch()"
								@blur="deferredCheckBusinessNameExists"
								data-cy="businessName"
								id="registerBusinessName"
							/>
							<p
								class="helpText"
								v-html="$t('text.businessNameHelp')"
							/>
						</div>
						<!-- Address -->
						<div class="field left-border">
							<v-label>{{ $t('text.businessAddress') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								autocomplete="street-address"
								:hide-details="streetAddressErrors.length === 0"
								:placeholder="$t('text.placeHolderAddress')"
								:error-messages="streetAddressErrors"
								@update:modelValue="step2Touched.streetAddress = true"
								@blur="step2Touched.streetAddress = true"
								v-model="contactInfoAddress.fields.contactAddresses.de[0].fields.streetAddress.de"
								data-cy="streetAddress"
								id="registerStreetAddress"
							/>
							<div style="display: flex;">
								<v-text-field
									variant="outlined"
									density="compact"
									autocomplete="postal-code"
									:hide-details="postalCodeErrors.length === 0"
									:placeholder="$t('text.placeHolderPostCode')"
									:error-messages="postalCodeErrors"
									@update:modelValue="step2Touched.zipCode = true"
									@blur="step2Touched.zipCode = true"
									v-model="contactInfoAddress.fields.contactAddresses.de[0].fields.zipCode.de"
									style="margin-right:10px"
									data-cy="zipCode"
									id="registerZipCode"
								/>
								<v-text-field
									variant="outlined"
									density="compact"
									autocomplete="address-level2"
									:hide-details="cityErrors.length === 0"
									:placeholder="$t('text.placeHolderCityTown')"
									:error-messages="cityErrors"
									@update:modelValue="step2Touched.city = true"
									@blur="step2Touched.city = true"
									v-model="contactInfoAddress.fields.contactAddresses.de[0].fields.city.de"
									data-cy="city"
									id="registerCity"
								/>
							</div>
							<v-select
								variant="outlined"
								density="compact"
								return-object
								autocomplete="country-name"
								:placeholder="$t('text.placeHolderCountry')"
								:error-messages="countryErrors"
								:hide-details="countryErrors.length === 0"
								:items="countries"
								:item-title="item => item.name ? item.name[selectedLocale] : ''"
								v-model="contactInfoAddress.fields.contactAddresses.de[0].fields.country"
								data-cy="country"
								id="registerCountry"
							/>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.businessPhone', { format: phoneFormat }) }}</v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								type="tel"
								:hide-details="businessPhoneErrors.length === 0"
								:error-messages="businessPhoneErrors"
								@update:modelValue="v$.contactInfoPhone.fields.contactDetail.de.$touch()"
								@blur="v$.contactInfoPhone.fields.contactDetail.de.$touch()"
								autocomplete="tel"
								v-model="contactInfoPhone.fields.contactDetail.de"
								data-cy="businessPhone"
								id="registerBusinessPhone"
							/>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.businessEmail') }} <span class="error-text">({{ $t('text.required')
									}})</span></v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								autocomplete="email"
								type="email"
								v-model="contactInfoEmail.fields.contactDetail.de"
								:error-messages="businessEmailErrors"
								:hide-details="businessEmailErrors.length === 0"
								data-cy="businessEmail"
								id="registerBusinessEmail"
							/>
						</div>
						<div class="field left-border">
							<v-label>{{ $t('text.accountNumber') }}</v-label>
							<v-text-field
								variant="outlined"
								density="compact"
								:hide-details="accountNumberErrors.length === 0"
								:error-messages="accountNumberErrors"
								@update:modelValue="step2Touched.accountNumber = true; v$.serviceProvider.fields.accountNumber.de.$touch()"
								@blur="step2Touched.accountNumber = true; v$.serviceProvider.fields.accountNumber.de.$touch()"
								v-model="serviceProvider.fields.accountNumber.de"
								data-cy="accountNumber"
								id="registerAccountNumber"
							/>
						</div>
						<IfFeatureFlag flag="FEATURE_TRADE_HUB">
							<div
								v-if="featureEnabled(Features.TRADE_HUB)"
								class="field left-border"
							>
								<v-label>{{ $t('text.accountType') }}</v-label>
								<p class="helpText">{{ $t('text.accountTypeHelpText') }}</p>
								<v-radio-group
									class="account-type-select"
									v-model="accountType"
									data-cy="account-type-group"
								>
									<v-radio
										value="serviceProvider"
										data-cy="service-provider-radio"
									>
										<template v-slot:label>
											<div><span>{{ $t('text.serviceProvider') }}</span><span>{{
												$t('text.serviceProviderTypeInfoText') }}</span></div>
										</template>
									</v-radio>
									<v-radio
										value="tourOperator"
										data-cy="tour-operator-radio"
									>
										<template v-slot:label>
											<div><span>{{ $t('text.tourOperatorAndReseller') }}</span><span>{{
												$t('text.tourOperatorTypeInfoText') }}</span></div>
										</template>
									</v-radio>
								</v-radio-group>
							</div>
						</IfFeatureFlag>
						<div
							v-if="showCaptcha"
							class="field left-border"
						>
							<v-label>{{ $t('text.captcha') }}</v-label>
							<p class="helpText">{{ $t('text.captchaHelpText') }}</p>
							<FriendlyCaptcha
								ref="captcha"
								:sitekey="captchaSitekey"
								:apiEndpoint="captchaApiEndpoint"
								:language="selectedLocale"
								@complete="captchaResponse = $event"
								@expire="captchaResponse = ''"
								@error="captchaResponse = ''"
							/>
						</div>
						<v-row
							align="center"
							justify="center"
							style="margin-top:20px"
						>
							<v-btn
								data-cy="btn-step-1"
								variant="flat"
								theme="dark"
								class="activeButton"
								style="margin-right:20px"
								@click.prevent="prev()"
							>{{ $t('text.previousStep') }}</v-btn>
							<v-btn
								id="btnSubmit"
								variant="flat"
								theme="dark"
								class="activeButton"
								@click="submit"
								data-cy="complete"
							>
								{{ $t('text.completeRegistration') }}
								<b v-if="oidc">&rarr; {{ oidc.client_id }}</b>
							</v-btn>
						</v-row>
					</v-form>
				</v-card-text>
			</v-card>
		</v-row>
	</Application>
</template>

<script>
import { useVuelidate } from "@vuelidate/core"
import { required, minLength, maxLength, email, sameAs, helpers } from "@vuelidate/validators"

// Keycloak PersonNameProhibitedCharactersValidator pattern - we use the same in order to avoid a keycloak error when the user is created in keycloak.
// Prohibits: < > & " $ % ! # ? § ; * ~ / | ^ = [ ] { } ( ) \ and control characters - taken from source code: https://raw.githubusercontent.com/keycloak/keycloak/main/services/src/main/java/org/keycloak/userprofile/validator/PersonNameProhibitedCharactersValidator.java
// Note: \x00-\x1F\x7F replaces Java's \p{Cntrl} for JavaScript compatibility
const personNameValidator = helpers.regex(/^[^<>&@"$%!#?§;*~/\\|^=\[\]{}()\x00-\x1F\x7F]+$/)
const phoneValidator = helpers.withMessage('', (value) => !value || /^[0-9+\s]+$/.test(value))
const genericTextValidator = helpers.withMessage('', (value) => {
	if (!value) return true

	for (const char of String(value)) {
		if ('<>[]{}|^~`$%!?#§*='.includes(char)) return false
		const code = char.charCodeAt(0)
		if (code < 32 || code === 127) return false
	}

	return true
})
const streetAddressValidator = helpers.withMessage('', (value) => !value || /^[\p{L}\p{N}\s.,'()\/-]+$/u.test(value))
const cityValidator = helpers.withMessage('', (value) => !value || /^[\p{L}\s.'()-]+$/u.test(value))
const zipCodeValidator = helpers.withMessage('', (value) => !value || /^[A-Za-z0-9\s-]+$/.test(value))
const accountNumberValidator = helpers.withMessage('', (value) => !value || /^[A-Za-z0-9\s\-/.]+$/.test(value))
const twoWordsValidator = helpers.withMessage('', (value) => typeof value === 'string' && value.trim().split(/\s+/).filter(w => w.length > 0).length >= 2)
import LanguageFlag from '@/components/common/LanguageFlag.vue'
import Common from '@/mixins/Common.vue'
import PasswordPolicy from '@/mixins/PasswordPolicy.vue'
import Stepper from '@/components/stepper/Stepper.vue'
import Application from '../applications/Application.vue'
import Authorization from '@/mixins/Authorization.vue'
import OIDC from './OIDC.ts'
import IfFeatureFlag from '@/components/ifFeatureFlag/IfFeatureFlag.vue'
import FriendlyCaptcha from '@/components/common/FriendlyCaptcha.vue'
import { Features } from '../../../../shared/features'

export default {
	name: 'Registration',
	components: { LanguageFlag, Stepper, Application, IfFeatureFlag, FriendlyCaptcha },
	mixins: [Common, Authorization, PasswordPolicy],
	validations() {
		return {
			serviceProvider: {
				marketplace: { required },
				password: { required, passwordPolicy: this.passwordPolicyRule(this.serviceProvider.fields.contactPersonEmail?.de) },
				repeatPassword: { required, sameAsPassword: sameAs(this.serviceProvider.password) },
				fields: {
					title: { de: { required, minLength: minLength(4), maxLength: maxLength(128), genericTextValidator, ...(this.isTradeHubClient ? { twoWordsValidator } : {}) } },
					contactPersonEmail: { de: { required, email, minLength: minLength(2), maxLength: maxLength(256) } },
					contactPersonPhone: { de: { phoneValidator } },
					accountNumber: { de: { accountNumberValidator } },
					businessEmail: { de: { required, email, minLength: minLength(2), maxLength: maxLength(256) } },
					language: { de: { required } }
				},
			},
			contactInfoName: {
				fields: {
					contactDetail: { de: { required, minLength: minLength(4), maxLength: maxLength(128), personNameValidator, ...(this.isTradeHubClient ? { twoWordsValidator } : {}) } }
				}
			},
			contactInfoEmail: {
				fields: {
					contactDetail: { de: { required, email, minLength: minLength(2), maxLength: maxLength(256) } }
				}
			},
			contactInfoPhone: {
				fields: {
					contactDetail: { de: { phoneValidator } }
				}
			},
		}
	},
	setup() {
		return {
			v$: useVuelidate()
		}
	},
	data() {
		return {
			loading: false,
			errorTitle: '',
			errorDetail: '',
			successTitle: '',
			successDetail: '',
			value: 0,
			query: false,
			persistHints: false,

			times: [],
			showPickerFrom: false,
			showPickerTo: false,

			countries: this.getCountries(),
			clients: null,
			clientRequired: false,
			activeBtn: '',

			businessNameAllowed: true,
			emailIsUnique: true,
			passwordApiErrors: [],

			// MYS-6325: captcha, only rendered for clients with the 'registration-captcha' feature
			captchaSitekey: '',
			captchaApiEndpoint: 'eu',
			captchaResponse: '',

			step: 1,
			runStep2Validation: false,
			showPassword: false,
			showRepeatPassword: false,
			step2Touched: {
				streetAddress: false,
				zipCode: false,
				city: false,
				accountNumber: false,
			},

			serviceProvider: {
				marketplace: '',
				clientId: '',
				password: '',
				repeatPassword: '',

				fields: {
					title: { de: '' },
					contactPersonName: { de: '' },
					contactPersonEmail: { de: '' },
					contactPersonPhone: { de: '' },
					accountNumber: { de: '' },
					language: { de: '' }
				}
			},
			contactInfoName: { sys: { id: 'id_1' }, fields: { contactInfoType: { de: { fields: { type: { de: 'Name' } } } }, contactDetail: { de: '' } } },
			contactInfoPhone: { sys: { id: 'id_1' }, fields: { contactInfoType: { de: { fields: { type: { de: 'Phone' } } } }, contactDetail: { de: '' } } },
			contactInfoEmail: { sys: { id: 'id_2' }, fields: { contactInfoType: { de: { fields: { type: { de: 'E-Mail' } } } }, contactDetail: { de: '' } } },
			contactInfoWebsite: { sys: { id: 'id_3' }, fields: { contactInfoType: { de: { fields: { type: { de: 'Website' } } } }, contactDetail: { de: '' } } },
			contactInfoAddress: {
				sys: { id: 'id_4' },
				fields: {
					contactInfoType: { de: { fields: { type: { de: 'Address' } } } },
					contactAddresses: {
						de: [{
							sys: { id: 'id_5' },
							fields: {
								streetAddress: { de: '' },
								zipCode: { de: '' },
								city: { de: '' },
								country: {},
							}
						}]
					}
				}
			},
			alignmentsAvailable: ['start', 'center', 'end', 'baseline', 'stretch'],
			alignment: 'center',
			dense: false,
			justifyAvailable: ['start', 'center', 'end', 'space-around', 'space-between'],
			justify: 'center',
			oidc: null,
			clientLocales: [],
			preferredLanguage: null,
			accountType: 'serviceProvider', // serviceProvider | tourOperator
			Features,
		}
	},
	computed: {
		marketplaceErrors() {
			const errors = []
			const { $dirty, required } = this.v$.serviceProvider.marketplace

			if (!$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.marketplaceRequiredError'))

			return errors
		},
		businessNameErrors() {
			const errors = []
			const { $dirty, required, minLength, maxLength, genericTextValidator, twoWordsValidator } = this.v$.serviceProvider.fields.title.de

			if (!this.runStep2Validation || !$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.businessNameRequired'))
			if (minLength.$invalid) errors.push(this.$t('text.businessNameMinChars'))
			if (maxLength.$invalid) errors.push(this.$t('text.businessNameMaxChars'))
			if (genericTextValidator.$invalid) errors.push(this.$t('text.fieldValUnsupportedCharacters'))
			if (!this.businessNameAllowed) errors.push(this.$t('text.businessNameExists'))
			if (twoWordsValidator?.$invalid) errors.push(this.$t('text.nameTwoWordsRequired'))

			return errors
		},
		contactPersonNameErrors() {
			const errors = []
			const { $dirty, required, minLength, maxLength, personNameValidator, twoWordsValidator } = this.v$.contactInfoName.fields.contactDetail.de

			if ((this.step === 2 && !this.runStep2Validation) || !$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.nameRequired'))
			if (minLength.$invalid) errors.push(this.$t('text.nameMinChars'))
			if (maxLength.$invalid) errors.push(this.$t('text.nameMaxChars'))
			if (personNameValidator.$invalid) errors.push(`${this.$t('text.nameInvalidCharacters')} < > & @ " $ % ! # ? § ; * ~ / \\ | ^ = [ ] { } ( )`)
			if (twoWordsValidator?.$invalid) errors.push(this.$t('text.nameTwoWordsRequired'))

			return errors
		},
		contactPersonEmailErrors() {
			const errors = []
			const fields = this.v$.serviceProvider.fields

			if (this.step === 2 && !this.runStep2Validation) return errors

			const { email, required, minLength, maxLength } = fields.contactPersonEmail.de

			if (email.$invalid) errors.push(this.$t('text.emailInvalid'))
			if (required.$invalid) errors.push(this.$t('text.emailRequired'))
			if (minLength.$invalid) errors.push(this.$t('text.emailMinChars'))
			if (maxLength.$invalid) errors.push(this.$t('text.emailMaxChars'))
			if (!this.emailIsUnique) errors.push(this.$t('text.registrationEmailExistsError'))

			return errors
		},
		contactPersonPhoneErrors() {
			const errors = []
			const { $dirty, phoneValidator } = this.v$.serviceProvider.fields.contactPersonPhone.de

			if ((this.step === 2 && !this.runStep2Validation) || !$dirty) return errors

			if (phoneValidator.$invalid) errors.push(this.$t('text.field-val-regexp-phone'))

			return errors
		},
		businessPhoneErrors() {
			const errors = []
			const value = this.contactInfoPhone.fields.contactDetail.de
			const { $dirty, phoneValidator } = this.v$.contactInfoPhone.fields.contactDetail.de

			if ((!this.runStep2Validation && !$dirty) || !value) return errors

			if (phoneValidator.$invalid) errors.push(this.$t('text.field-val-regexp-phone'))

			return errors
		},
		businessEmailErrors() {
			const errors = []
			const { $dirty, email, required, minLength, maxLength } = this.v$.contactInfoEmail.fields.contactDetail.de

			if (!this.runStep2Validation || !$dirty) return errors

			if (email.$invalid) errors.push(this.$t('text.emailInvalid'))
			if (required.$invalid) errors.push(this.$t('text.emailRequired'))
			if (minLength.$invalid) errors.push(this.$t('text.emailMinChars'))
			if (maxLength.$invalid) errors.push(this.$t('text.emailMaxChars'))

			return errors
		},
		passwordErrors() {
			const errors = []
			const { $dirty, required } = this.v$.serviceProvider.password

			if (!$dirty) return this.passwordApiErrors

			if (required.$invalid) errors.push(this.$t('text.passwordRequired'))
			errors.push(...this.passwordPolicyMessages(this.serviceProvider.password, this.serviceProvider.fields.contactPersonEmail?.de), ...this.passwordApiErrors)

			return errors
		},
		repeatPasswordErrors() {
			const errors = []
			const { $dirty, sameAsPassword } = this.v$.serviceProvider.repeatPassword

			if (!$dirty) return errors

			if (sameAsPassword.$invalid) errors.push(this.$t('text.repeatPasswordSame'))

			return errors
		},
		streetAddressErrors() {
			const errors = []
			const [{ fields }] = this.contactInfoAddress?.fields.contactAddresses.de ?? [{}]
			const value = fields?.streetAddress?.de ?? ''

			if (!this.runStep2Validation && !this.step2Touched.streetAddress) return errors

			if (!value.length) errors.push(this.$t('text.businessAddressRequiredError'))
			else if (!streetAddressValidator.$validator(value)) errors.push(this.$t('text.fieldValStreetAddressFormat'))

			return errors
		},
		postalCodeErrors() {
			const errors = []
			const value = this.contactInfoAddress?.fields?.contactAddresses?.de?.[0]?.fields?.zipCode?.de ?? ''

			if (!this.runStep2Validation && !this.step2Touched.zipCode) return errors

			if (!value.length) errors.push(this.$t('text.businessAddressRequiredError'))
			else if (!zipCodeValidator.$validator(value)) errors.push(this.$t('text.fieldValZipCodeFormat'))

			return errors
		},
		cityErrors() {
			const errors = []
			const value = this.contactInfoAddress?.fields?.contactAddresses?.de?.[0]?.fields?.city?.de ?? ''

			if (!this.runStep2Validation && !this.step2Touched.city) return errors

			if (!value.length) errors.push(this.$t('text.businessAddressRequiredError'))
			else if (!cityValidator.$validator(value)) errors.push(this.$t('text.fieldValCityFormat'))

			return errors
		},
		accountNumberErrors() {
			const errors = []
			const value = this.serviceProvider.fields.accountNumber.de
			const { $dirty, accountNumberValidator } = this.v$.serviceProvider.fields.accountNumber.de

			if ((!this.runStep2Validation && !$dirty && !this.step2Touched.accountNumber) || !value) return errors

			if (accountNumberValidator.$invalid) errors.push(this.$t('text.fieldValAccountNumberUnsupportedCharacters'))

			return errors
		},
		countryErrors() {
			if (!this.runStep2Validation) return []
			const errors = []
			if (this.contactInfoAddress && this.contactInfoAddress.fields.contactAddresses.de.length > 0) {
				if (Object.keys(this.contactInfoAddress.fields.contactAddresses.de[0].fields.country).length === 0 || !this.contactInfoAddress.fields.contactAddresses.de[0].fields.country) {
					errors.push(this.$t('text.businessAddressRequiredError'))
				}
			}
			return errors
		},
		preferredLanguageErrors() {
			const errors = []
			const { $dirty, required } = this.v$.serviceProvider.fields.language.de

			if (!$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.preferredLanguageRequired'))

			return errors
		},
		serviceProviderClient() {
			const clientId = this.serviceProvider?.clientId
			if (!clientId || !this.clients) {
				return
			}
			return this.clients.find(x => x.sys.id === clientId)
		},
		isTradeHubClient() {
			return this.serviceProviderClient?.fields?.isTradeHub?.de === true
		},
		serviceProviderMarketplace() {
			return this.serviceProvider?.marketplace
		},
		/*
		 * MYS-6325: only clients with the 'registration-captcha' feature require a captcha.
		 *
		 * featureEnabled() reads $store.state.selectedClient, which is committed both by
		 * getHostClient() (clientId/spaceId query param or domain -> marketplace lookup) and by
		 * the serviceProviderMarketplace watcher (dropdown), so this stays correct on every path.
		 */
		captchaRequired() {
			return this.featureEnabled(Features.REGISTRATION_CAPTCHA)
		},
		/*
		 * Only mount the widget once the user actually reaches step 2, so that the challenge is
		 * not solved inside a hidden container (the steps use v-show) and the token is fresh when
		 * it is submitted. Step 2 stays mounted at step 3, so the token survives submit().
		 */
		showCaptcha() {
			return this.captchaRequired && !!this.captchaSitekey && this.step >= 2
		}
	},
	watch: {
		selectedLocale() {
			if (this.countries) { this.countries.sort(this.compareCountry) }
		},
		serviceProviderMarketplace() {
			const foundClient = this.clients.find(x => x.fields.title.de === this.serviceProviderMarketplace)
			const foundClientId = foundClient?.sys?.id
			if (this.serviceProviderMarketplace && this.serviceProvider.clientId !== foundClientId) {
				this.serviceProvider.clientId = foundClientId
				this.$store.commit('setSelectedClient', foundClient)
			}
		},
		async serviceProviderClient() {
			if (this.serviceProviderClient) {
				await this.getClientLocales(this.serviceProviderClient.fields.contentfulSpace.de, this.serviceProviderClient.fields.contentfulEnvironment.de)
			}
		},
		preferredLanguage() {
			this.setLocale(this.preferredLanguage)
		}
	},
	methods: {
		async setLocale(locale) {
			this.selectedLocale = locale
			this.serviceProvider.fields.language.de = locale
			this.$root.$i18n.locale = locale
			this.$vuetify.locale.current = locale
		},
		prev() {
			this.clear();
			this.step--;
		},
		next() {
			this.clear();

			if (this.step === 1) {
				this.v$.$touch()

				if (!this.contactPersonNameErrors.length &&
					!this.contactPersonEmailErrors.length &&
					!this.contactPersonPhoneErrors.length &&
					!this.passwordErrors.length &&
					!this.repeatPasswordErrors.length &&
					!this.preferredLanguageErrors.length) {

					this.step++;
				}

			} else if (this.step === 2) {
				this.runStep2Validation = true
				this.step2Touched.streetAddress = true
				this.step2Touched.zipCode = true
				this.step2Touched.city = true
				this.step2Touched.accountNumber = true
				this.v$.serviceProvider.fields.title.de.$touch()
				this.v$.contactInfoEmail.fields.contactDetail.de.$touch()
				this.v$.contactInfoPhone.fields.contactDetail.de.$touch()
				this.v$.serviceProvider.fields.accountNumber.de.$touch()

				if (!this.businessNameErrors.length &&
					!this.businessEmailErrors.length &&
					!this.businessPhoneErrors.length &&
					!this.accountNumberErrors.length &&
					!this.streetAddressErrors.length &&
					!this.postalCodeErrors.length &&
					!this.cityErrors.length &&
					!this.countryErrors.length) {

					this.step++;
				}
			}
		},
		clear() {
			this.errorTitle = "";
			this.errorDetail = "";
			this.successTitle = "";
			this.successDetail = "";
		},
		/* Server Functions */
		getHostClient() {
			// allow setting of the clientId through the url query
			const params = window.location.search.replace(/^\?/, '')
			this.$httpGet(`/host-client?${params}&hostUrl=${window.location.hostname}`)
				.then(async res => {
					var hasDefaultClient = false;

					if (res?.client) {
						hasDefaultClient = true;
						var client = res.client

						if (client) {
							this.serviceProvider.clientId = client.sys.id
							await this.$store.commit('setSelectedClient', client)
						}
					}

					if (!hasDefaultClient) {
						this.clientRequired = true
					}
				}).catch(error => {
					this.errorTitle = this.$t('text.ERROR');

					if (error.response) {
						this.errorDetail = error.response.data.error;
					} else {
						this.errorDetail = error;
					}
				});
		},
		async getClients() {
			try {
				const res = await this.$httpGet(`/clients`)
				this.clients = res
			}
			catch (error) {
				this.showError(error.response ? error.response?.data.error : error)
			}
		},
		/*
		 * MYS-6325: the sitekey is public (it ships inside the widget markup) and comes from api1
		 * so that it can differ per environment without rebuilding the dashboard.
		 * Failing to load it just means showCaptcha stays false - the server still enforces the
		 * captcha, so a registration for a protected client would be rejected with a clear error.
		 */
		async getCaptchaConfig() {
			try {
				const res = await this.$httpGet('/captcha-config')
				this.captchaSitekey = res?.sitekey ?? ''
				this.captchaApiEndpoint = res?.apiEndpoint ?? 'eu'
			}
			catch (error) {
				console.error('Could not load captcha configuration', error)
			}
		},
		async getClientLocales(spaceId, environmentId) {
			try {
				const result = await this.$httpGet(`/client-locales?spaceId=${spaceId}&environmentId=${environmentId}`)
				this.clientLocales = []
				for (const locale of result) {
					this.fillClientLocales(locale)
				}
				if (this.preferredLanguage && !this.clientLocales.some(x => x.locale === this.preferredLanguage)) {
					// fallback to eng
					this.preferredLanguage = this.clientLocales.find(x => x.locale === 'en')?.locale ?? this.clientLocales[0]?.locale
				}
			}
			catch (error) {
				this.showError(error.response ? error.response?.data.error : error)
			}
		},
		fillClientLocales(locale) {
			switch (locale) {
				case 'de':
					this.clientLocales.push({ locale, title: 'Deutsch' })
					break
				case 'en':
					this.clientLocales.push({ locale, title: 'English' })
					break
				case 'fr':
					this.clientLocales.push({ locale, title: 'Français' })
					break
				case 'it':
					this.clientLocales.push({ locale, title: 'Italiano' })
					break
				case 'nl':
					this.clientLocales.push({ locale, title: 'Nederlands' })
					break
			}
		},
		showError(detail, title) {
			this.errorTitle = title ?? this.$t('text.ERROR')
			this.errorDetail = detail
		},
		resetCaptcha() {
			if (!this.showCaptcha) return
			this.captchaResponse = ''
			this.$refs.captcha?.reset()
		},
		/*
		 * MYS-6325: the registration endpoints can now answer 400 { error: 'captchaVerificationFailed' }
		 * and 429 { error: 'tooManyRequests' }, so those keys need translating before display.
		 *
		 * Note on the error shape: $httpFetch throws the parsed response body itself (see
		 * plugins/HttpUtil.js), so the key sits on error.error - there is no axios-style
		 * error.response wrapper. The other shapes are kept as fallbacks.
		 */
		errorDetailFor(error) {
			const serverError = error?.error ?? error?.response?.data?.error ?? error?.message

			if (serverError === 'tooManyRequests') return this.$t('text.tooManyRequests')
			if (serverError === 'captchaVerificationFailed') return this.$t('text.captchaVerificationFailed')

			return serverError ?? error
		},
		async submit() {
			// this.v$.$touch()
			this.next()
			if (this.step !== 3) return

			if (this.clientRequired && this.serviceProvider.marketplace === "") {
				this.errorTitle = this.$t('text.ERROR');
				this.errorDetail = this.$t('text.marketplaceRequiredError');

			} else if (this.showCaptcha && !this.captchaResponse) {
				// MYS-6325: the server enforces this too - this is only to give immediate feedback.
				this.errorTitle = this.$t('text.ERROR');
				this.errorDetail = this.$t('text.captchaRequiredError');

			} else {
				this.loading = true

				//Contact Details
				this.serviceProvider.fields.contactInfos = {
					de: [
						this.contactInfoName,
						this.contactInfoPhone,
						this.contactInfoEmail,
						this.contactInfoWebsite,
						this.contactInfoAddress
					]
				}

				this.serviceProvider.fields.contactPersonName.de = this.contactInfoName.fields.contactDetail.de
				var data = {
					serviceProvider: this.serviceProvider,
					clientId: this.serviceProvider.clientId,
					language: this.selectedLocale,
					selectedLocale: this.selectedLocale,
					autolinkUser: false,
					accountType: this.accountType,
					captchaResponse: this.captchaResponse
				};

				try {
					await this.$httpPost('/registration', data)

					this.successTitle = this.$t('text.ACCOUNT_CREATED')
					this.successDetail = this.$t('text.registrationSuccess')

					const ok = await this.login(data.serviceProvider.fields.contactPersonEmail.de, data.serviceProvider.password)
					if (this.oidc) {
						await OIDC.complete(this.oidc, this.$store.state.loggedInUser.kc_token)
						return
					}
					if (ok) this.$emit('show-dashboard')

					this.loading = false
				} catch (error) {
					this.loading = false;
					this.errorTitle = this.$t('text.ERROR');

					// MYS-6325: captcha responses are single use, so the widget has to be reset
					// before the user can retry - otherwise the retry fails as 'response_duplicate'.
					this.resetCaptcha()

					// TODO: we currently also show ACCOUNT CREATED if it was not actually created!!!
					// MYS-7422: $httpFetch throws the parsed response body ({ error } or { message }),
					// so match on the raw server message regardless of which shape it arrived in.
					// Nothing renders at step 3, so always send the user back to a step they can fix.
					const serverError = error?.error ?? error?.response?.data?.error ?? error?.message
					if (serverError === 'E-mail Address is already associated with an account') {
						this.emailIsUnique = false
						this.step = 1
						this.errorDetail = this.$t('text.registrationEmailExistsError');
					} else if (serverError === 'passwordPolicy') {
						// MYS-7479: only Keycloak knows its list of common passwords
						this.passwordApiErrors = this.passwordPolicyApiMessages(error)
						this.step = 1
						this.errorDetail = this.passwordApiErrors.join(' ')
					} else if (serverError === 'Service provider is already registered') {
						this.businessNameAllowed = false
						this.step = 2
						this.errorDetail = this.$t('text.registrationProviderExistsError');
					} else {
						this.step = 2
						// Covers the MYS-6325 captchaVerificationFailed / tooManyRequests keys.
						this.errorDetail = this.errorDetailFor(error)
					}
				}
			}
		},
		onPasswordInput() {
			this.passwordApiErrors = []
			this.v$.serviceProvider.password.$touch()
		},
		deferredCheckBusinessNameExists() {
			setTimeout(() => this.checkBusinessNameExists(), 0)
		},
		deferredCheckEmailExists() {
			setTimeout(() => this.checkEmailExists(), 0)
		},
		async checkBusinessNameExists() {
			this.loading = true
			try {
				const res = await this.$httpGet(`/check-businessname?title=${encodeURIComponent(this.serviceProvider.fields.title.de)}`)
				this.businessNameAllowed = res.businessNameAllowed
			}
			catch (error) {
				// These checks are rate limited (MYS-6325), so they can legitimately fail.
				// Without this the loading overlay would stay up forever.
				this.showError(this.errorDetailFor(error))
			}
			finally {
				this.loading = false
			}
		},
		async checkEmailExists() {
			this.loading = true
			try {
				const res = await this.$httpGet(`/check-email?email=${encodeURIComponent(this.serviceProvider.fields.contactPersonEmail.de)}`)
				if (res.emailExists) {
					this.emailIsUnique = false
				} else if (res.emailExists === false) {
					this.emailIsUnique = true
				}
			}
			catch (error) {
				this.showError(this.errorDetailFor(error))
			}
			finally {
				this.loading = false
			}
		},
		getSteps() { return [this.$t('text.step1Label'), this.$t('text.step2Label')] },
		navigateBack() {
			if (this.oidc) {
				this.$router.push('/auth' + location.search)
			}
			else {
				this.$router.push('/' + location.search)
			}
		},
	},
	async mounted() {
		this.oidc = await OIDC.init(this.$route.query)
		this.getHostClient()
		this.getClients()
		this.getCaptchaConfig()
		if (Array.isArray(this.locales)) {
			for (const locale of this.locales) {
				this.fillClientLocales(locale.code)
			}
		}
		this.preferredLanguage = this.$route.query.lang ?? 'en'
	},
}
</script>

<style scoped>
	.page {
		width: 100%;
		max-width: 600px;
	}

	.error {
		color: red;
	}

	.button {
		height: 48px !important;
		background: linear-gradient(0deg, #efefef 0%, #ffffff 100%) !important;
		border: thin #dddddd solid !important;
		display: flex !important;
		/*justify-content: space-around !important;*/
		align-items: center !important;
		border-radius: 5px !important;
		font-family: 'Inter', sans-serif;
		font-size: 12pt !important;
		text-transform: none !important;
		letter-spacing: 0 !important;
	}

	.activeButton {
		height: 48px !important;
		background: #62c522 !important;
		border: thin #58b01e solid !important;
		display: flex !important;
		justify-content: space-around !important;
		align-items: center !important;
		border-radius: 5px !important;
		font-family: 'Inter', sans-serif;
		font-size: 12pt !important;
		font-weight: normal !important;
		letter-spacing: 0 !important;
		text-transform: none !important;
	}

	.headline {
		color: #999999;
		font-weight: bold;
		font-size: 16pt !important;
	}

	#rounded-card {
		border-radius: 50%;
		width: 25px;
		height: 25px;
		padding: 0;
	}

	.stepLabel {
		font-size: 10pt;
	}

	.oval {
		width: 16px;
		height: 16px;
		box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.2);
		border: solid 1px rgba(0, 0, 0, 0.1);
		background-color: #f4f4f4;
		margin-right: 3px !important;
		margin-left: -10px !important;
	}

	.ovalActive {
		width: 16px;
		height: 16px;
		border: solid 1px #008ec3;
		background-color: #009dd7;
		margin-right: 3px !important;
		margin-left: -10px !important;
	}

	.activityActive {
		background-color: #00aeef !important;
		box-shadow: none !important;

		border-radius: 30px !important;
		border: solid 1px #009cd6 !important;
		font-size: 14pt !important;
		color: #ffffff !important;
		height: 32px !important;
		margin: 5px;
		text-transform: none !important;
		letter-spacing: 0 !important;
	}

	.activityInactive {
		background: #ffffff !important;
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
		border: thin #dddddd solid !important;

		border-radius: 30px !important;
		font-size: 14pt !important;
		color: #000000 !important;
		height: 32px !important;
		margin: 5px;
		text-transform: none !important;
		letter-spacing: 0 !important;
	}

	.v-list-item__title {
		font-size: 14pt !important;
	}

	.navbarComponent {
		width: 100%;
		height: 100%;
		padding: 0;
		border-left: 1px solid rgb(180, 195, 202) !important;
		border-right: 1px solid rgb(180, 195, 202) !important;
	}

	#componentContainer {
		width: 260px;
		min-width: 260px;
		background-color: transparent !important;
		margin-top: -7px;
		margin-left: 0;
	}

	.mainStyle-nocomp {
		color: #999999;
		font-weight: normal;
		font-size: 16pt !important;
	}

	.listItemStyle-nocomp {
		padding-top: 10px;
		position: relative;
		float: left;
	}

	.input-group--disabled.checkbox .input-group__input {
		color: #000 !important;
	}

	.input-group--disabled.input-group--select label {
		color: #000 !important;
	}
</style>

<style scoped lang="scss">
	.account-type-select {
		.v-selection-control {
			height: 60px !important;
			min-height: 60px;

			&:first-child {
				border-bottom: 1px solid #8F8F8F;
			}
		}

		.v-label {
			>div {
				display: flex;
				flex-direction: column;
			}

			span:first-child {
				font-size: 15px;
				font-weight: 400;
				line-height: 20px;
				letter-spacing: -0.037px;
			}

			span:last-child {
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
				letter-spacing: 0.001px;
				color: #8F8F8F;
			}
		}
	}
</style>

<style>
	body .v-stepper__header {
		height: 60px;
	}

	body .v-stepper__step {
		padding: 0 20px;
	}
</style>

<style lang="scss">
	.v-list:has(.preferred-language-list-item) {
		padding: 0px;

		.preferred-language-list-item {
			padding-inline: 12px;
			min-height: 60px;
			padding-top: 7px;
			padding-bottom: 7px;
			border-bottom: 1px solid #e5e5e5;

			&:hover {
				cursor: pointer !important;
			}

			.v-list-item__overlay,
			.v-list-item-title,
			.v-list-item__spacer {
				display: none;
			}

			.v-list-item-action {
				margin-inline-end: 0px;
			}

			&.v-list-item--active {
				color: inherit !important;
			}
		}
	}
</style>
